import { supabase } from '../lib/supabase'
import {
  Assumptions,
  CostType,
  DEFAULT_ASSUMPTIONS,
  isBillOverdue,
  money,
} from '../lib/commission'
import {
  canReverse,
  estimateWayOff,
  expectedCostTypes,
  reversalDate,
} from '../lib/commissionV2'
import { RateResult, StandardCost, rateFor, reconcile } from '../lib/standardCosts'
import { categoryForCostType } from '../lib/costCategories'
import {
  ESTIMATE_SOURCE_OVERRIDE,
  ESTIMATE_SOURCE_STANDARD,
  isAccrualSource,
} from '../lib/estimateSource'
import { isSuperAdmin } from '../lib/permissions'
import type { User } from '../types'
import { cosAccountFor } from './plService'

/**
 * Standard cost accruals, and reconciliation when the real bill turns up.
 *
 * Winnie, 7 Sep: "where a bill is missing beyond the cutoff, accrue a standard cost, flag it
 * estimated - unconfirmed, chase the supplier, and reconcile against the actual on arrival."
 *
 * Four separate things, and they are kept separate here on purpose:
 *
 *   `accrualCandidates`  - what is missing and old enough to accrue. Reports; changes nothing.
 *   `applyAccrual`       - writes ONE accrual, and only when a rate says what it should be.
 *   `chaseList`          - what a person should ring somebody about.
 *   `reconcileArrival`   - the real invoice lands and the accrual reverses through the P&L.
 *                          Under spec v2 it has no path back to commission at all.
 *
 * The split matters because only the middle one writes. Anything that reads can be run on a
 * whole page of vehicles without consequence, which is what makes it safe to show.
 */

const today = () => new Date().toISOString().slice(0, 10)

export interface AccrualCandidate {
  dealId: string
  costType: CostType
  /** What the rate card says, including its refusal when it has nothing to say. */
  rate: RateResult
  /** Why this is a candidate at all. */
  reason: string
  /** True when there is an amount and it could be written right now. */
  actionable: boolean
}

/**
 * The two settings v2 still has.
 *
 * The row on commission_assumptions used to carry a holdback percentage, a true-up window and
 * a materiality threshold as well. Spec section 1 lists all three under Removed from v1, and
 * the columns have been dropped along with the engine that read them.
 */
export async function loadAssumptions(): Promise<Assumptions> {
  try {
    const { data } = await supabase.from('commission_assumptions').select('*').eq('id', 1).maybeSingle()
    if (!data) return DEFAULT_ASSUMPTIONS
    return {
      default_commission_pct: Number(data.default_commission_pct),
      missing_bill_cutoff_days: Number(data.missing_bill_cutoff_days),
    }
  } catch {
    return DEFAULT_ASSUMPTIONS
  }
}

/* --------------------------------------------------------------------------------------
 * The rate card
 * ------------------------------------------------------------------------------------ */

export async function loadStandardCosts(): Promise<StandardCost[]> {
  const { data, error } = await supabase
    .from('standard_costs')
    .select('id, cost_type, label, basis, band_min_km, band_max_km, amount, active, notes')
    .order('cost_type')
    .order('band_min_km')
  if (error) throw error
  return ((data || []) as unknown as StandardCost[]).map((r) => ({ ...r, amount: Number(r.amount) }))
}

export interface StandardCostInput {
  id?: string | null
  cost_type: CostType
  label: string
  basis: 'flat' | 'distance_band'
  band_min_km?: number | null
  band_max_km?: number | null
  amount: number
  active?: boolean
  notes?: string | null
}

export async function saveStandardCost(input: StandardCostInput): Promise<void> {
  /* The database has the same rules as a check constraint, and will say no. Saying no here
     first means the person gets a sentence instead of a Postgres error. */
  if (!input.label || !input.label.trim()) throw new Error('Give this rate a name.')
  if (!Number.isFinite(Number(input.amount)) || Number(input.amount) < 0) {
    throw new Error('A standard rate needs an amount of zero or more.')
  }
  if (input.basis === 'distance_band') {
    if (input.band_min_km === null || input.band_min_km === undefined || Number(input.band_min_km) < 0) {
      throw new Error('A distance band needs a starting distance.')
    }
    if (
      input.band_max_km !== null &&
      input.band_max_km !== undefined &&
      Number(input.band_max_km) <= Number(input.band_min_km)
    ) {
      throw new Error('The end of a distance band has to be further than its start.')
    }
  }

  const row = {
    cost_type: input.cost_type,
    label: input.label.trim(),
    basis: input.basis,
    band_min_km: input.basis === 'distance_band' ? Number(input.band_min_km) : null,
    band_max_km:
      input.basis === 'distance_band' &&
      input.band_max_km !== null &&
      input.band_max_km !== undefined &&
      String(input.band_max_km) !== ''
        ? Number(input.band_max_km)
        : null,
    amount: money(Number(input.amount)),
    active: input.active !== false,
    notes: input.notes || null,
    updated_at: new Date().toISOString(),
  }

  if (input.id) {
    const { error } = await supabase.from('standard_costs').update(row).eq('id', input.id)
    if (error) throw error
  } else {
    const { error } = await supabase.from('standard_costs').insert([row])
    if (error) throw error
  }
}

/**
 * Retire a rate rather than delete it.
 *
 * An accrual made last month was made at a rate, and deleting the rate makes that number
 * unexplainable. Inactive rates are ignored by `rateFor` and stay readable.
 */
export async function setStandardCostActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase
    .from('standard_costs')
    .update({ active, updated_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

/* --------------------------------------------------------------------------------------
 * Accruals
 * ------------------------------------------------------------------------------------ */

interface DealLite {
  id: string
  sold_at: string | null
  sale_date: string | null
  transport_distance_km: number | null
  transport_arranged_by: string | null
}

interface ExpenseLite {
  id: string
  deal_id: string | null
  cost_type: CostType | null
  amount: number | null
  bill_status: string | null
  incurred_on: string | null
  estimated_amount: number | null
  supplier: string | null
  invoice_ref: string | null
}

/**
 * The cost types worth accruing when they are absent.
 *
 * Only inflow costs and freight out. Not PurchasePrice - a vehicle with no purchase price is
 * not a missing bill, it is an incomplete deal, and accruing a "standard" purchase price
 * would be inventing the single largest number on the record.
 */
const ACCRUABLE: CostType[] = ['Inspection', 'FreightIn', 'Reconditioning', 'FreightOut']

/**
 * What could be accrued on one vehicle, and what the rate card says about each.
 *
 * Read-only. Returns the refusals as well as the amounts, because "freight is not accrued
 * because no distance is recorded" is the sentence that gets somebody to type the distance.
 */
export async function accrualCandidates(dealId: string): Promise<AccrualCandidate[]> {
  const a = await loadAssumptions()
  const [rates, d, e] = await Promise.all([
    loadStandardCosts(),
    supabase
      .from('deals')
      .select('id, sold_at, sale_date, transport_distance_km, transport_arranged_by')
      .eq('id', dealId)
      .maybeSingle(),
    supabase
      .from('expenses')
      .select('id, deal_id, cost_type, amount, bill_status, incurred_on, estimated_amount, supplier, invoice_ref')
      .eq('deal_id', dealId),
  ])
  if (d.error) throw d.error
  if (e.error) throw e.error
  if (!d.data) return []

  const deal = d.data as unknown as DealLite
  const expenses = (e.data || []) as unknown as ExpenseLite[]
  const have = new Set(expenses.map((x) => x.cost_type).filter(Boolean) as CostType[])
  /* Which costs this vehicle is expected to carry at all. Shared with the commission gate so
     the chase list and the Commission Report cannot end up disagreeing about the same car.
     Reconditioning stays on the accruable list below without being on the expected list: it
     is accrued when it is missing, but its absence never blocks a calculation. */
  const expected = expectedCostTypes({ transportArrangedBy: deal.transport_arranged_by })

  /* The clock starts at the sale, because that is the event that should have produced the
     bills. A vehicle sold yesterday is not chasing anybody. */
  const anchor = deal.sold_at || deal.sale_date
  const overdue = isBillOverdue(anchor, today(), a)

  const out: AccrualCandidate[] = []
  for (const ct of ACCRUABLE) {
    if (have.has(ct)) continue
    /* Freight arranged by the buying dealer is somebody else's bill to pay, not a bill of
       ours that has gone missing. */
    if (ct === 'FreightOut' && !expected.includes(ct)) continue

    const r = rateFor(rates, ct, ct === 'FreightIn' || ct === 'FreightOut' ? deal.transport_distance_km : null)
    out.push({
      dealId,
      costType: ct,
      rate: r,
      reason: !anchor
        ? 'This vehicle has no sale date, so there is no clock on its missing bills yet.'
        : overdue
          ? `No ${ct} cost has been entered and it is more than ${a.missing_bill_cutoff_days} days since the sale.`
          : `No ${ct} cost yet, but it is still inside the ${a.missing_bill_cutoff_days} day window.`,
      actionable: Boolean(anchor) && overdue && r.amount !== null,
    })
  }
  return out
}

export interface AccrualResult {
  ok: boolean
  code: string
  message: string
  expenseId?: string
}

/**
 * Write one accrual.
 *
 * Deliberately one at a time and deliberately refusing without a rate. A bulk "accrue
 * everything" that quietly skips half the vehicles teaches nobody which half.
 */
export async function applyAccrual(dealId: string, costType: CostType): Promise<AccrualResult> {
  const candidates = await accrualCandidates(dealId)
  const c = candidates.find((x) => x.costType === costType)
  if (!c) {
    return {
      ok: false,
      code: 'not_a_candidate',
      message: 'There is already a cost of this type on the vehicle, so nothing is accrued.',
    }
  }
  if (c.rate.amount === null) {
    return { ok: false, code: c.rate.code, message: c.rate.message || 'No rate applies to this cost.' }
  }
  if (!c.actionable) {
    return { ok: false, code: 'not_yet', message: c.reason }
  }

  const now = new Date().toISOString()
  const amount = c.rate.amount
  const { data, error } = await supabase
    .from('expenses')
    .insert([
      {
        deal_id: dealId,
        cost_type: costType,
        category: categoryForCostType(costType),
        amount,
        estimated_amount: amount,
        estimate_source: ESTIMATE_SOURCE_STANDARD,
        estimate_applied_at: now,
        bill_status: 'Estimated',
        description: `Estimated - unconfirmed. ${c.rate.rate ? c.rate.rate.label : 'Standard rate'}.`,
        incurred_on: today(),
      },
    ])
    .select('id')
  if (error) throw error

  return {
    ok: true,
    code: 'accrued',
    message: `Accrued ${aud(amount)} as an estimate. It is flagged unconfirmed until the invoice arrives.`,
    expenseId: data && data[0] ? (data[0] as { id: string }).id : undefined,
  }
}

/* --------------------------------------------------------------------------------------
 * Overriding a cost by hand
 * ------------------------------------------------------------------------------------ */

export interface OverrideResult {
  ok: boolean
  code: string
  message: string
  expenseId?: string
}

/**
 * A super admin sets a cost figure by hand so commission can be struck on this vehicle.
 *
 * Nick, 12 Sep: *"the freight need to be taken into account to calculate the commissions.
 * the super admin can over ride it."*
 *
 * Freight was always part of the maths - `EXPECTED_COST_TYPES` has carried FreightIn and
 * FreightOut from the start, and landed cost adds every line, estimated or actual. What was
 * missing was a figure: with no invoice and no rate card, `accrualPlan` blocks rather than
 * accruing, because a standard rate of nothing is indistinguishable from a cost of nothing
 * and zero is the estimate that flatters the margin most. All 153 sold vehicles sat there.
 *
 * The override is not a new kind of record. It writes the same estimated cost line the rate
 * card would have written, so it flows through landed cost, the accrual reversal and the P&L
 * by the paths that already exist - the only difference is that its defence is a named
 * person and a reason instead of a rate. When the supplier invoice finally arrives,
 * `reconcileArrival` corrects the P&L and, per spec section 3, leaves the commission alone.
 *
 * Four refusals, none of them negotiable:
 *
 * - **Not a super admin.** This is the whole point of the control.
 * - **The commission is already locked.** Section 9: a locked commission is never
 *   recalculated. Changing the cost underneath it would make the locked figure a lie.
 * - **An invoice is already on file for this cost.** An invoice beats a person's estimate,
 *   and overriding it would hide a real number behind a typed one.
 * - **No reason.** A figure nobody has to justify is the silent zero wearing a hat.
 */
export async function overrideCostEstimate(input: {
  dealId: string
  costType: CostType
  amount: number
  reason: string
  user: User | null
}): Promise<OverrideResult> {
  const { dealId, costType, user } = input
  const reason = String(input.reason || '').trim()
  const amount = Number(input.amount)

  if (!isSuperAdmin(user)) {
    return {
      ok: false,
      code: 'not_super_admin',
      message: 'Only a super admin can set a cost by hand. Ask one to do it, or load the rate card.',
    }
  }
  if (costType === 'PurchasePrice') {
    return {
      ok: false,
      code: 'not_overridable',
      message:
        'The purchase price is not something to set by hand. A vehicle with no purchase price is an incomplete deal rather than a late bill.',
    }
  }
  if (!Number.isFinite(amount) || amount < 0) {
    return { ok: false, code: 'bad_amount', message: 'Enter the amount as a number, zero or more.' }
  }
  if (reason.length < 3) {
    return {
      ok: false,
      code: 'no_reason',
      message: 'Say why this figure is the right one. It is what the number rests on instead of an invoice.',
    }
  }

  /* Locked first, before anything is read or written. */
  const { data: rec } = await supabase
    .from('commission_records')
    .select('locked_at')
    .eq('deal_id', dealId)
    .is('superseded_at', null)
    .is('voided_at', null)
    .not('locked_at', 'is', null)
    .limit(1)
  const lockedAt = ((rec || [])[0] as { locked_at?: string } | undefined)?.locked_at
  if (lockedAt) {
    return {
      ok: false,
      code: 'already_locked',
      message:
        'Commission on this vehicle was calculated and locked on ' +
        String(lockedAt).slice(0, 10) +
        '. A locked commission is never recalculated, so the costs behind it do not move either.',
    }
  }

  const { data: existing, error: readErr } = await supabase
    .from('expenses')
    .select('id, bill_status, estimate_source, amount')
    .eq('deal_id', dealId)
    .eq('cost_type', costType)
  if (readErr) return failed(readErr)

  const rows = (existing || []) as {
    id: string
    bill_status: string | null
    estimate_source: string | null
    amount: number | string | null
  }[]

  const invoiced = rows.find((r) => (r.bill_status || 'Received') === 'Received')
  if (invoiced) {
    return {
      ok: false,
      code: 'already_invoiced',
      message:
        'There is already an invoice on file for this cost, and an invoice beats an estimate. Edit the cost in Cost of Sales if the figure is wrong.',
    }
  }

  const now = new Date().toISOString()
  const label = String(costType).replace(/([a-z])([A-Z])/g, '$1 $2')
  const description = 'Set by hand - no invoice and no standard rate. ' + reason

  /* An existing estimate is replaced rather than added to, so one cost type never ends up
     with two figures racing each other into landed cost. */
  const mine = rows.find((r) => isAccrualSource(r.estimate_source)) || rows[0]

  if (mine) {
    const { error } = await supabase
      .from('expenses')
      .update({
        amount,
        estimated_amount: amount,
        estimate_source: ESTIMATE_SOURCE_OVERRIDE,
        estimate_applied_at: now,
        bill_status: 'Estimated',
        override_reason: reason,
        description,
      })
      .eq('id', mine.id)
    if (error) return failed(error)
    await logOverride('cost_override_updated', user, mine.id, dealId, costType, amount, reason, mine.amount)
    return {
      ok: true,
      code: 'updated',
      message: label + ' is now ' + aud(amount) + ' on this vehicle. Commission can be struck on it.',
      expenseId: mine.id,
    }
  }

  const { data, error } = await supabase
    .from('expenses')
    .insert([
      {
        deal_id: dealId,
        cost_type: costType,
        category: categoryForCostType(costType),
        amount,
        estimated_amount: amount,
        estimate_source: ESTIMATE_SOURCE_OVERRIDE,
        estimate_applied_at: now,
        bill_status: 'Estimated',
        override_reason: reason,
        description,
        incurred_on: today(),
        created_by: user?.id || null,
      },
    ])
    .select('id')
  if (error) return failed(error)

  const id = data && data[0] ? (data[0] as { id: string }).id : undefined
  if (id) await logOverride('cost_override_set', user, id, dealId, costType, amount, reason, null)

  return {
    ok: true,
    code: 'set',
    message: label + ' set to ' + aud(amount) + ' on this vehicle. Commission can be struck on it.',
    expenseId: id,
  }
}

/**
 * A database refusal comes back as an answer rather than an exception.
 *
 * Throwing left the button reading "Saving..." for ever and told the person nothing, which
 * is how a check-constraint rejection looked the first time this ran: the write was refused
 * and the screen simply sat there. Whatever the database objects to, somebody should be able
 * to read it and say what happened.
 */
function failed(error: { message?: string } | null): OverrideResult {
  console.error('Could not set the cost by hand', error)
  return {
    ok: false,
    code: 'write_failed',
    message:
      'The figure could not be saved: ' +
      ((error && error.message) || 'the database refused the change') +
      '. Nothing was changed on this vehicle.',
  }
}

/** Who set it, when, to what, and why. Never throws: the figure matters more than the note. */
async function logOverride(
  action: string,
  user: User | null,
  expenseId: string,
  dealId: string,
  costType: CostType,
  amount: number,
  reason: string,
  previous: number | string | null,
): Promise<void> {
  try {
    await supabase.from('audit_log').insert([
      {
        actor_user_id: user?.id || null,
        actor_name: user?.full_name || null,
        action,
        target_name: expenseId,
        details: {
          dealId,
          costType,
          amount,
          reason,
          previousAmount: previous === null ? null : Number(previous),
          source: ESTIMATE_SOURCE_OVERRIDE,
        },
        created_at: new Date().toISOString(),
      },
    ])
  } catch (e) {
    console.error('Could not write the audit entry for this override (non-blocking)', e)
  }
}

/* --------------------------------------------------------------------------------------
 * Chasing
 * ------------------------------------------------------------------------------------ */

export interface ChaseItem {
  expenseId: string
  dealId: string | null
  costType: CostType | null
  supplier: string | null
  invoiceRef: string | null
  amount: number | null
  estimatedAmount: number | null
  daysWaiting: number | null
  message: string
}

/**
 * Everything sitting on an estimate, oldest first.
 *
 * Winnie asked for this to feed the same chase logic as accounts receivable, and the shape is
 * the same shape: a thing owed, how long it has been owed, and who to ask. It does not send
 * anything - it produces the list a person works from.
 */
export async function chaseList(): Promise<ChaseItem[]> {
  const { data, error } = await supabase
    .from('expenses')
    .select('id, deal_id, cost_type, supplier, invoice_ref, amount, estimated_amount, estimate_applied_at, bill_status')
    .eq('bill_status', 'Estimated')
    .is('estimate_reconciled_at', null)
  if (error) throw error

  const now = Date.now()
  return ((data || []) as unknown as (ExpenseLite & { estimate_applied_at: string | null })[])
    .map((r) => {
      const applied = r.estimate_applied_at ? new Date(r.estimate_applied_at).getTime() : NaN
      const days = Number.isNaN(applied) ? null : Math.floor((now - applied) / 86400000)
      return {
        expenseId: r.id,
        dealId: r.deal_id,
        costType: r.cost_type,
        supplier: r.supplier,
        invoiceRef: r.invoice_ref,
        amount: r.amount === null ? null : Number(r.amount),
        estimatedAmount: r.estimated_amount === null ? null : Number(r.estimated_amount),
        daysWaiting: days,
        message:
          days === null
            ? 'Estimated, waiting on the supplier invoice.'
            : `Estimated ${days} day${days === 1 ? '' : 's'} ago and the invoice still has not arrived.`,
      }
    })
    .sort((x, y) => (y.daysWaiting || 0) - (x.daysWaiting || 0))
}

/* --------------------------------------------------------------------------------------
 * Reconciliation on arrival — v2
 * ------------------------------------------------------------------------------------ */

export interface ReconcileArrivalResult {
  ok: boolean
  code: string
  message: string
  variance: number | null
  /** The date the reversal journal posts on. */
  reversalOn: string | null
  /** True when the estimate was wildly out — informational, never a blocker. */
  estimateWasWayOff: boolean
}

/**
 * The real invoice arrives and replaces the estimate.
 *
 * Under Winnie's build spec v2 this is a **pure P&L accrual reversal and nothing else**. It
 * does not read the commission record, it does not recalculate anything, and there is
 * deliberately no code path from here to `commission_records`. Section 4 of the spec:
 * *"variance between an estimated cost and its actual invoice flows through the company's P&L
 * via the accrual reversal, not back into a salesperson's already-paid commission."*
 *
 * v1 called `reopenDecision` here and could move a commission. That is removed, not disabled
 * — a dormant path back to commission is exactly the thing that gets switched on by accident.
 *
 * The estimate stays on the row beside the actual. Overwriting it would make the variance
 * invisible the moment it was corrected, which is the whole of what "reconcile" means here.
 */
export async function reconcileArrival(
  expenseId: string,
  actualAmount: number,
  opts?: {
    supplier?: string | null
    invoiceRef?: string | null
    /** Last month-end that has been locked, so the reversal posts in a period that is open. */
    monthLockedThrough?: string | null
    preview?: boolean
  },
): Promise<ReconcileArrivalResult> {
  const { data: exp, error: expErr } = await supabase
    .from('expenses')
    .select('id, deal_id, category, cost_type, amount, estimated_amount, bill_status, estimate_source, estimate_reconciled_at')
    .eq('id', expenseId)
    .maybeSingle()
  if (expErr) throw expErr
  if (!exp) {
    return { ok: false, code: 'not_found', message: 'That cost line no longer exists.', variance: null, reversalOn: null, estimateWasWayOff: false }
  }

  const row = exp as unknown as ExpenseLite & {
    category: string | null
    estimate_source: string | null
    estimate_reconciled_at: string | null
  }

  const gate = canReverse({
    cost_type: row.cost_type,
    amount: row.amount,
    bill_status: row.bill_status as 'Received' | 'Estimated' | null,
    is_accrual: isAccrualSource(row.estimate_source),
    accrual_reversed: Boolean(row.estimate_reconciled_at),
  })
  if (!gate.ok) {
    return { ok: false, code: gate.code, message: gate.reason, variance: null, reversalOn: null, estimateWasWayOff: false }
  }

  const rec = reconcile(row.estimated_amount, actualAmount)
  if (!rec) {
    return {
      ok: false,
      code: 'not_an_estimate',
      message: 'This cost was never an estimate, so there is nothing to reconcile it against.',
      variance: null,
      reversalOn: null,
      estimateWasWayOff: false,
    }
  }

  const postOn = reversalDate(today(), opts && opts.monthLockedThrough)
  const wayOff = estimateWayOff(row.estimated_amount, actualAmount)
  const tail =
    ' The accrual reverses through the P&L on ' +
    (postOn || 'the current period') +
    '. Commission on this vehicle does not change.' +
    (wayOff ? ' The estimate was a long way out — worth revisiting the standard rate.' : '')

  if (opts && opts.preview) {
    return { ok: true, code: 'preview', message: rec.message + tail, variance: rec.variance, reversalOn: postOn, estimateWasWayOff: wayOff }
  }

  const now = new Date().toISOString()
  const update: Record<string, unknown> = {
    amount: money(actualAmount),
    bill_status: 'Received',
    estimate_reconciled_at: now,
  }
  if (opts && opts.supplier) update.supplier = opts.supplier
  if (opts && opts.invoiceRef) update.invoice_ref = opts.invoiceRef

  const { error: upErr } = await supabase.from('expenses').update(update).eq('id', expenseId)
  if (upErr) throw upErr
  /* Spec section 3: post the accrual reversal journal, DR Accrued Expense / CR the cost
     account the estimate was charged to, for the ORIGINAL estimate - the actual is booked
     through the normal cost flow. Section 4 is why this entry has to exist: commission does
     not move, so the variance has to land in the P&L, and this is what puts it there.

     Written as a draft. The account CODES are the accountant to supply; posting to a guessed
     ledger is worse than posting late, so the names are filled from Winnie's own chart and
     the codes stay null until somebody who knows them fills them in.

     A failed journal write does not undo the reconciliation - the cost line is already
     correct and the reversal is recoverable from the row - but it is surfaced rather than
     swallowed. */
  const estimated = money(row.estimated_amount || 0)
  const { error: jErr } = await supabase.from('accrual_journals').insert({
    expense_id: expenseId,
    deal_id: row.deal_id,
    cost_type: row.cost_type,
    estimated_amount: estimated,
    actual_amount: money(actualAmount),
    amount: estimated,
    variance: rec.variance,
    credit_account_name: cosAccountFor(row.category),
    post_on: postOn || today(),
    posted_in_period: postOn && postOn !== today() ? 'next_month' : 'current',
  })
  const journalNote = jErr
    ? ' The cost line is corrected, but the reversal journal could not be written: ' + jErr.message
    : ' A draft reversal journal has been written for ' + aud(estimated) + '; its Xero account codes still need the accountant.'

  return { ok: true, code: 'reversed', message: rec.message + tail + journalNote, variance: rec.variance, reversalOn: postOn, estimateWasWayOff: wayOff }
}

function aud(n: number): string {
  return `${n < 0 ? '-$' : '$'}${Math.abs(money(n)).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}
